// Copyright 2016 Sorama B.V.

import { autoinject, BindingEngine, computedFrom } from "aurelia-framework";
import { Container, MD5FileHash, ReorderProperties } from "datatypes/SoramaLocalApi";
import { GenericMeasurement } from "datatypes/SoramaWebApi";
import { StringExtensions } from "extensions/StringExtensions";
import { ILocalApi } from "libraries/WebApi/ILocalApi";
import { IWebRequestHandler, WebRequestPriority } from "libraries/WebRequestHandler/IWebRequestHandler";
import { ClientStatus, IClientService } from "services/Client/IClientService";
import { IStorageService, ITypedStorageContainerSet } from "services/Storage/IStorageService";
import { FileDownloader } from "./../../libraries/FileDownloader/FileDownloader";

export class TypedStorageContainerSet implements ITypedStorageContainerSet {
    private storageService: StorageService;
    private refCounter = 0;
    private _type: string;

    private refreshPromise: Promise<Container[]>;

    public get type(): string {
        return this._type;
    }

    @computedFrom("_isRefreshing")
    public get isRefreshing(): boolean {
        return this.refreshPromise !== undefined;
    }

    constructor(storageService: StorageService, type: string) {
        this.storageService = storageService;
        this._type = type;
        this.containers = [];
        this.refCounter++;
    }

    /**
     * Gets the currently available containers (to update containers use Refresh())
     */
    public containers: Container[];

    public refresh(): Promise<Container[]> {
        if (!this.refreshPromise) {
            this.refreshPromise = this.refreshAction();
        }

        return this.refreshPromise;
    }

    private refreshAction(): Promise<Container[]> {
        return this.storageService
            .enumerate(this._type)
            .then((containers) => {
                // Update existing container array
                this.containers.splice(0, this.containers.length, ...containers);

                return this.containers;
            })
            .catch(() => {
                // Catch errors that occur during refresh, just return an empty array
                return <Container[]>[];
            })
            .finally(() => {
                this.refreshPromise = undefined;
            });
    }

    public getReference(): ITypedStorageContainerSet {
        this.refCounter++;
        return this;
    }

    public dispose(): void {
        this.refCounter--;
        if (this.refCounter === 0) {
            this.storageService.disposeStorageContainer(this);
        }
    }
}

@autoinject
export class StorageService implements IStorageService {
    private webRequestHandler: IWebRequestHandler;
    private typedContainers: TypedStorageContainerSet[] = [];
    private localApi: ILocalApi;

    constructor(
        bindingEngine: BindingEngine,
        clientService: IClientService,
        webRequestHandler: IWebRequestHandler,
        localApi: ILocalApi
    ) {
        this.webRequestHandler = webRequestHandler;
        this.localApi = localApi;

        bindingEngine.propertyObserver(clientService, "status").subscribe(this.clientStatusChanged);

        bindingEngine.propertyObserver(clientService, "storagePath").subscribe(this.clientStoragePathChanged);
    }

    //#region Create, Read and Delete Calls
    public create(type: string): Promise<string> {
        return this.localApi.storage.then((uri) => {
            return this.webRequestHandler
                .createPostRequest<string>(uri + "/" + type)
                .send()
                .then((x) => {
                    return this.enumerate(type).then(() => x.content);
                });
        });
    }

    public enumerate(type: string): Promise<Container[]> {
        return this.localApi.storage.then((uri) => {
            return this.webRequestHandler
                .createGetRequest<Container[]>(uri + "/" + type)
                .withPriority(WebRequestPriority.LowPriority)
                .send()
                .then((x) => {
                    return x.content;
                });
        });
    }

    public getFiles(containerId: string): Promise<string[]> {
        return this.localApi.storage.then((uri) => {
            return this.webRequestHandler
                .createGetRequest<string[]>(uri + "/" + containerId)
                .send()
                .then((x) => {
                    return x.content;
                });
        });
    }

    public delete(containerId: string): Promise<void> {
        return this.localApi.storage.then((uri) => {
            return this.webRequestHandler
                .createDeleteRequest<void>(uri + "/" + containerId)
                .send()
                .then(() => {
                    return this.updateTypedContainers();
                });
        });
    }

    public load(containerId: string): Promise<Container> {
        return this.localApi.storage.then((uri) => {
            return this.webRequestHandler
                .createGetRequest<Container>(uri + "/" + containerId + "/Container")
                .send()
                .then((x) => {
                    const container = x.content;

                    if (!container.metadata) {
                        return this.getMetadata(containerId).then((metadata: unknown) => {
                            container.metadata = metadata;
                            return container;
                        });
                    }

                    return container;
                });
        });
    }

    public getSorMetadata(sorId: string): Promise<GenericMeasurement> {
        return this.localApi.storage.then((uri) => {
            return this.webRequestHandler
                .createGetRequest<GenericMeasurement>(uri + "/" + sorId + "/MeasurementInfo")
                .send()
                .then((x) => {
                    return x.content;
                });
        });
    }

    public async getTypedStorageContainerSet(type: string): Promise<ITypedStorageContainerSet> {
        // Check if there are existing containers of this type
        const typedContainer = this.typedContainers.find((x) => x.type === type);
        if (typedContainer) {
            await typedContainer.refresh();
            return Promise.resolve(typedContainer.getReference());
        } else {
            // Create a new one
            const newTypedContainer = new TypedStorageContainerSet(this, type);
            this.typedContainers.push(newTypedContainer);
            await newTypedContainer.refresh();
            return newTypedContainer;
        }
    }

    public disposeStorageContainer(container: TypedStorageContainerSet): void {
        let index = -1;
        if ((index = this.typedContainers.indexOf(container)) === -1) return;

        this.typedContainers.splice(index, 1);
    }

    //#endregion

    //#region Pseudo-Properties
    public getSorPath(sorId: string): Promise<string> {
        return this.localApi.storage.then((uri) => {
            return this.webRequestHandler
                .createGetRequest<string>(uri + "/" + sorId + "/SorPath")
                .send()
                .then((x) => {
                    return x.content;
                });
        });
    }

    public getPath(containerId: string): Promise<string> {
        return this.localApi.storage.then((uri) => {
            return this.webRequestHandler
                .createGetRequest<string>(uri + "/" + containerId + "/Path")
                .send()
                .then((x) => {
                    return x.content;
                });
        });
    }

    public getMetadata<T>(containerId: string): Promise<T> {
        return this.localApi.storage.then((uri) => {
            return this.webRequestHandler
                .createGetRequest<T>(uri + "/" + containerId + "/Metadata")
                .withPriority(WebRequestPriority.LowPriority)
                .send()
                .then((x) => {
                    return <T>x.content;
                });
        });
    }

    public getMetadataValue<T>(containerId: string, key: string): Promise<T> {
        return this.localApi.storage.then((uri) => {
            return this.webRequestHandler
                .createGetRequest<T>(uri + "/" + containerId + "/Metadata?key=" + key)
                .withPriority(WebRequestPriority.LowPriority)
                .send()
                .then((x) => x.content);
        });
    }

    public getMD5Hash(containerId: string, file: string): Promise<MD5FileHash> {
        return this.localApi.storage.then((uri) => {
            return this.webRequestHandler
                .createGetRequest<MD5FileHash>(uri + "/" + containerId + "/MD5?file=" + file)
                .send()
                .then((x) => x.content);
        });
    }

    public async getFileSize(containerId: string, file: string): Promise<number> {
        return this.localApi.storage.then((uri) => {
            return this.webRequestHandler
                .createGetRequest<number>(uri + "/" + containerId + "/FileSize?file=" + file)
                .send()
                .then((x) => x.content);
        });
    }

    public async getRecordingSizeMap(containerId: string): Promise<Map<string, number>> {
        return this.localApi.storage.then((uri) => {
            return this.webRequestHandler
                .createGetRequest<Map<string, number>>(uri + "/" + containerId + "/RecordingGroupSize")
                .send()
                .then((x) => new Map(Object.entries(x.content)));
        });
    }

    public async getRecordingSizeMaps(containerIds: string[]): Promise<Map<string, Map<string, number>>> {
        if (containerIds.length === 0) return new Map<string, Map<string, number>>();

        return this.localApi.storage.then((uri) => {
            return this.webRequestHandler
                .createGetRequest<Map<string, Map<string, number>>>(
                    uri + "/RecordingGroupSizes?containerIds=" + containerIds.join("&containerIds=")
                )
                .send()
                .then((x) => new Map(Object.entries(x.content)));
        });
    }

    public async getRecordingGroupSize(containerId: string): Promise<number> {
        const recordingSizeMap = await this.getRecordingSizeMap(containerId);
        const recordingSizeSum = Array.from(recordingSizeMap.values()).reduce((acc, cur) => acc + cur, 0);
        return recordingSizeSum;
    }

    public async getRecordingGroupSizes(containerId: string[]): Promise<Map<string, number>> {
        const recordingGroupSizeMaps = await this.getRecordingSizeMaps(containerId);
        const recordingGroupSizes = new Map<string, number>();
        for (const [recordingGroupId, recordingSizeDict] of recordingGroupSizeMaps) {
            const recordingSizeMap = new Map(Object.entries(recordingSizeDict));
            const recordingSizeSum = Array.from(recordingSizeMap.values()).reduce((acc, cur) => acc + cur, 0);
            recordingGroupSizes.set(recordingGroupId, recordingSizeSum);
        }
        return recordingGroupSizes;
    }

    public getRaw(containerId: string, file: string, offset: number, size: number): Promise<Blob> {
        return this.localApi.storage.then((uri) => {
            return this.webRequestHandler
                .createGetRequest<Blob>(
                    StringExtensions.format(
                        uri + "/{0}/Raw?file={1}&offset={2}&size={3}",
                        containerId,
                        file,
                        offset.toString(),
                        size.toString()
                    )
                )
                .withBinaryResponse()
                .send()
                .then((x) => x.content);
        });
    }

    public getRawSor(sorId: string): Promise<Blob> {
        return this.localApi.storage.then((uri) => {
            return this.webRequestHandler
                .createGetRequest<Blob>(StringExtensions.format(uri + "/{0}/RawSor", sorId))
                .withBinaryResponse()
                .send()
                .then((x) => x.content);
        });
    }

    private async getDataZipUrl(groupIds: string[], zipFileName?: string): Promise<string> {
        return this.localApi.storage.then((uri) => {
            return StringExtensions.format(
                uri +
                    "/RecordingGroup/Zip?groupId=" +
                    groupIds.join("&groupId=") +
                    (zipFileName ? "&fileName=" + zipFileName : "")
            );
        });
    }

    public exportDataZip(groupIds: string[], zipFileName?: string): Promise<void> {
        if (groupIds.length === 0) {
            return Promise.resolve();
        }
        this.getDataZipUrl(groupIds, zipFileName).then((url) => FileDownloader.downloadAsFileFromURL(url, zipFileName));
    }

    public async getDataZip(groupIds: string[], zipFileName?: string): Promise<Blob> {
        if (groupIds.length === 0) {
            throw new Error("No container measurements provided");
        }

        const url = await this.getDataZipUrl(groupIds, zipFileName);

        return this.webRequestHandler
            .createGetRequest<Blob>(url)
            .withBinaryResponse()
            .send()
            .then((x) => x.content);
    }

    public async getSor(sorId: string): Promise<Blob> {
        const storageApi = await this.localApi.storage;

        return this.webRequestHandler
            .createGetRequest<Blob>(storageApi + "/" + sorId + "/Sor")
            .withBinaryResponse()
            .send()
            .then((x) => x.content);
    }

    public async getSorZip(sorIds: string[], zipFileName?: string): Promise<Blob> {
        if (sorIds.length === 0) {
            throw new Error("No sor files provided");
        }

        const storageApi = await this.localApi.storage;

        return this.webRequestHandler
            .createGetRequest<Blob>(
                storageApi +
                    "/Sor/Zip?sorId=" +
                    sorIds.join("&sorId=") +
                    (zipFileName ? "&fileName=" + zipFileName : "")
            )
            .withBinaryResponse()
            .send()
            .then((x) => x.content);
    }

    public updateMetadata(containerId: string, key: string, value: unknown): Promise<void> {
        return this.localApi.storage.then((uri) => {
            if (typeof value === "string") {
                value = encodeURIComponent(value);
            }

            const apiCall = uri + "/" + containerId + "/Metadata?key=" + encodeURIComponent(key);
            const request = this.webRequestHandler.createPutRequest<void>(apiCall, value);

            if ((typeof value === "string" && value) || typeof value === "number") {
                request.withValue();
            }

            return request
                .withPriority(WebRequestPriority.LowPriority)
                .send()
                .then(() => {
                    this.updateTypedContainers();
                });
        });
    }

    public updateMetadataSet(containerId: string, kvps: { [key: string]: unknown }): Promise<void> {
        return this.localApi.storage.then((uri) => {
            const apiCall = uri + "/" + containerId + "/Metadata";
            return this.webRequestHandler
                .createPutRequest<void>(apiCall, kvps)
                .withPriority(WebRequestPriority.LowPriority)
                .send()
                .then(() => {
                    this.updateTypedContainers();
                });
        });
    }

    public updateFileMetadata(containerId: string, file: string, key: string, value: unknown): Promise<void> {
        return this.localApi.storage.then((uri) => {
            return this.webRequestHandler
                .createPutRequest<void>(uri + "/" + containerId + "/Metadata?file=" + file + "&key=" + key, value)
                .withPriority(WebRequestPriority.LowPriority)
                .send()
                .then(() => {
                    this.updateTypedContainers();
                });
        });
    }

    public toMeasurementStorage(
        containerId: string,
        measurementId: string,
        containerFile: string,
        measurementFile: string
    ): Promise<void> {
        return this.localApi.storage.then((uri) => {
            return this.webRequestHandler
                .createPostRequest<void>(
                    uri +
                        "/" +
                        containerId +
                        "/Measurement?measurementId=" +
                        measurementId +
                        "&file=" +
                        containerFile +
                        "&newFile=" +
                        measurementFile
                )
                .send()
                .then(() => {});
        });
    }

    public toMeasurementStorageReordered(measurementId: string, reorderProperties: ReorderProperties): Promise<void> {
        return this.localApi.storage.then((uri) => {
            return this.webRequestHandler
                .createPostRequest<void>(
                    uri + "/MeasurementReordered?measurementId=" + measurementId,
                    reorderProperties
                )
                .send()
                .then(() => {});
        });
    }

    public createMeasurementStorageTextFile(
        measurementId: string,
        fileName: string,
        fileContent: string
    ): Promise<void> {
        return this.localApi.storage.then((uri) => {
            return this.webRequestHandler
                .createPostRequest<void>(
                    uri + "/Measurement/" + measurementId + "/Text?file=" + fileName + "&text=" + fileContent
                )
                .send()
                .then(() => {});
        });
    }

    public areMeasurementsAvailable(measurements: GenericMeasurement[]): Promise<{ [key: string]: boolean }> {
        return this.localApi.storage.then((uri) => {
            return this.webRequestHandler
                .createPostRequest<{ [key: string]: boolean }>(uri + "/Measurement/Available", measurements)
                .send()
                .then((x) => x.content);
        });
    }

    public openInExplorer(path: string): Promise<void> {
        return this.localApi.storage.then((uri) => {
            return this.webRequestHandler
                .createGetRequest<void>(uri + "/OpenInExplorer?path=" + path)
                .send()
                .then(() => {});
        });
    }
    //#endregion

    private clientStatusChanged = (newStatus: ClientStatus, oldStatus: ClientStatus): void => {
        const newAvailable = newStatus === ClientStatus.Available || newStatus === ClientStatus.OutdatedVersion;
        const oldAvailable = oldStatus === ClientStatus.Available || oldStatus === ClientStatus.OutdatedVersion;

        if (newAvailable !== oldAvailable) {
            // Update typed containers
            this.updateTypedContainers();
        }
    };

    private clientStoragePathChanged = (newValue: string, oldValue: string): void => {
        if (newValue === oldValue || oldValue === undefined) return;

        // Update typed containers
        this.updateTypedContainers();
    };

    private updateTypedContainers(): Promise<void> {
        const refreshPromises = this.typedContainers.map((x) => x.refresh());
        return Promise.all(refreshPromises).then(() => {});
    }
}
